import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Compass, Home, Mail, Sparkles } from 'lucide-react';
import Header from '../component/Header';
import Footer from '../component/Footer';

const NotFound: React.FC = () => {
  const navigate = useNavigate();

  const quickLinks = [
    {
      title: 'Home',
      description: 'Start from our main overview and core features',
      path: '/',
      icon: Home,
    },
    {
      title: 'Course Library',
      description: 'Explore practical AI, automation, and productivity courses',
      path: '/library/',
      icon: BookOpen,
    },
    {
      title: 'Pricing & Plans',
      description: 'Find the right membership for your learning goals',
      path: '/pricing/',
      icon: Sparkles,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-zinc-950 text-zinc-100 selection:bg-amber-500 selection:text-black">
      <Header />

      <main className="flex-1 relative flex items-center justify-center py-20 px-6 overflow-hidden">
        {/* Ambient background glow & grid */}
        <div
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            backgroundImage:
              'linear-gradient(#3f3f46 1px, transparent 1px), linear-gradient(90deg, #3f3f46 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-2xl w-full mx-auto text-center">
          {/* Status pill badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-semibold tracking-wide uppercase mb-8">
            <Compass className="w-3.5 h-3.5 animate-spin-slow" />
            <span>404 Error • Page Not Found</span>
          </div>

          {/* Big graphical 404 display */}
          <div className="relative select-none mb-6">
            <h1 className="text-8xl sm:text-9xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-zinc-100 via-zinc-400 to-zinc-700">
              404
            </h1>
          </div>

          <p className="text-zinc-300 text-base sm:text-lg mb-8 max-w-lg mx-auto leading-relaxed">
            The page you are looking for doesn't exist, may have been moved, or the link might be broken.
          </p>

          {/* Primary Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 mb-14">
            <button
              onClick={() => navigate(-1)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-zinc-800 bg-zinc-900/60 hover:bg-zinc-850 hover:border-zinc-700 text-zinc-200 text-sm font-semibold transition shadow-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
            <Link
              to="/"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold transition shadow-lg shadow-amber-500/20"
            >
              <Home className="w-4 h-4" />
              Back to Homepage
            </Link>
          </div>

          {/* Helpful Navigation Cards */}
          <div className="text-left border-t border-zinc-800/80 pt-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-4 text-center sm:text-left">
              Helpful Destinations
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {quickLinks.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="group p-4 rounded-xl bg-zinc-900/40 border border-zinc-800/70 hover:border-zinc-700 hover:bg-zinc-900 transition flex flex-col justify-between"
                  >
                    <div>
                      <div className="w-8 h-8 rounded-lg bg-zinc-800/60 border border-zinc-700/50 flex items-center justify-center text-amber-400 mb-3 group-hover:scale-105 transition">
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <h3 className="text-sm font-semibold text-zinc-200 group-hover:text-amber-400 transition mb-1">
                        {item.title}
                      </h3>
                      <p className="text-xs text-zinc-500 line-clamp-2">
                        {item.description}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Need help footer note */}
          <div className="mt-10 flex items-center justify-center gap-2 text-xs text-zinc-500">
            <span>Still experiencing an issue?</span>
            <a
              href="mailto:support@learnaihub.net"
              className="inline-flex items-center gap-1 text-amber-400 hover:underline"
            >
              <Mail className="w-3.5 h-3.5" />
              Contact Support
            </a>
          </div>
        </div>
      </main>

      <Footer hideCta />
    </div>
  );
};

export default NotFound;
